import { Fragment, useMemo } from 'react';
import { FC } from 'react';
import { BookingStatus, IBooking, IFlightSegment, ITraveller } from '@/shared/api/aviabooking';
import { morph } from '@/shared/lib/word-morph';
import { selectAirlinesMap, TransporterIcon } from '@/entities/aviabooking-reference';
import FlightSvg from '@/shared/assets/images/AirplaneTakeoff.svg'
import { useAppDispatch, useAppSelector } from '@/shared/lib/redux';
import { BookingStatusConfig, fetchBookings, setPage } from '@/entities/booking';
import { useBookingPaymentModal, useBookingTicketsModal, useCancelBookingModal } from '@/features/booking-payment';
import { useAcknowledgePriceChangeModal } from '@/features/booking-payment/lib/use-acknowledge-price-change-modal';
import { resolveCancelAvailability } from '@/entities/booking/lib/cancel-availability';
import { getRefundStatusColor, getRefundStatusLabel } from '@/entities/booking/config/refund-status';

const FlightSegmentBar: FC<{ segments: IFlightSegment[] }> = ({ segments }) => {
    return (
        <div className="flex items-center">
            <div className="w-3 h-3 rounded-full bg-color-primary"></div>
            {segments.map((_, index) => (
                <Fragment key={index}>
                    {index > 0 && (
                        <div className="flex-1 h-0.5 bg-gray-300 mx-2">
                            <div className="w-1 h-1 bg-gray-500 rounded-full mx-auto"></div>
                        </div>
                    )}
                    <div className="flex-1 h-0.5 bg-color-primary"></div>
                </Fragment>
            ))}
            <div className="w-3 h-3 rounded-full bg-color-primary"></div>
        </div>
    )
}

const PriceDisplay: FC<{ amount: number, currency: string }> = ({ amount, currency }) => {
    return (
        <div className="text-right">
            <div className="text-2xl font-bold text-gray-900">
                {amount?.toLocaleString()} {currency}
            </div>
        </div>
    )
}

const TravellerInfo: FC<{ traveller: ITraveller }> = ({ traveller }) => {
    const typeLabel = traveller.type === 'ADT' ? 'Взрослый' :
        traveller.type === 'CNN' ? 'Ребенок' : 'Младенец'
    return (
        <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-900 font-medium">
                {traveller.last_name} {traveller.first_name}
            </span>
            <span className="text-gray-500">•</span>
            <span className="text-gray-500">{typeLabel}</span>
        </div>
    )
}

const BookingStatusBadge: FC<{ status: number }> = ({ status }) => {
    const config = BookingStatusConfig[status]
    return (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
            {config.label}
        </span>
    )
}

const FlightTimeInfo: FC<{ datetime: string }> = ({
    datetime,
}) => {
    const date = new Date(datetime)
    const hours = date.getHours().toString().padStart(2, '0')
    const minutes = date.getMinutes().toString().padStart(2, '0')

    return (
        <span>
            {hours}:{minutes}
        </span>
    )
}

const AirportCode: FC<{ code: string }> = ({ code }) => {
    return (
        <div className="text-sm text-gray-500 font-medium">{code}</div>
    )
}

export interface BookingProps {
    booking: IBooking
}

export const Booking: FC<BookingProps> = ({ booking }) => {
    const dispatch = useAppDispatch()
    const airlines = useAppSelector(selectAirlinesMap)
    const { segments, price_summary, travellers, status, booking_id } = booking
    const firstSegment = segments[0]
    const lastSegment = segments[segments.length - 1]
    const { openPaymentModal } = useBookingPaymentModal()
    const { openModal: openCancelBookingModal } = useCancelBookingModal()
    const { openModal: openTicketsModal } = useBookingTicketsModal()
    const { openModal: openAcknowledgeModal } = useAcknowledgePriceChangeModal()

    const cancelAvailability = resolveCancelAvailability(booking)
    const showPayment = booking.status === BookingStatus.StatusBooked
    const showTicket = booking.status === BookingStatus.StatusTicketed
    const showAcknowledge = booking.price_changed
    const refundStatusLabel = getRefundStatusLabel(booking.refund_status)
    const refundStatusColor = getRefundStatusColor(booking.refund_status)

    // После успешной отмены список перечитывается с первой страницы: карточка должна
    // показать новый статус, состояние возврата и уже недоступную отмену, а не
    // остаться в состоянии «до отмены». Список не очищается заранее — если запрос
    // упадёт, администратор увидит прежние брони, а не ложное «нет активных».
    const handleCancelled = () => {
        dispatch(setPage(1))
        dispatch(fetchBookings())
    }

    // Доступность отмены определяется только признаком сервера, поэтому контрол
    // рендерится в обеих раскладках футера — иначе price_changed стал бы локальным
    // правилом, скрывающим разрешённую сервером отмену (FR-adm-6).
    const cancelControl = cancelAvailability.canCancel
        ? (
            <button onClick={() => openCancelBookingModal(booking, handleCancelled)} className="bg-color-danger cursor-pointer text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                Отменить
            </button>
        )
        : (
            <span className="max-w-sm text-right text-xs text-gray-500">
                {cancelAvailability.reasonText}
            </span>
        )

    const uniqueAirlines = useMemo(() => {
        const uniqueAirlines = new Set(segments.map(segment => segment.marketing_airline))
        return Array.from(uniqueAirlines)
    }, [segments])

    const totalDuration = useMemo(() => {
        const depTime = new Date(segments[0].dep_date_time)
        const arrTime = new Date(segments[segments.length - 1].arr_date_time)
        return Math.round((arrTime.getTime() - depTime.getTime()) / (1000 * 60))
    }, [segments])

    const stops = segments.length - 1

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow duration-200">
            <div className="flex items-center justify-between p-4 border-b border-gray-100 bg-gray-50">
                <div className="flex items-center gap-3">
                    <BookingStatusBadge status={status} />
                    {refundStatusLabel && refundStatusColor && (
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${refundStatusColor}`}>
                            {refundStatusLabel}
                        </span>
                    )}
                    <span className="text-sm text-gray-500">#{booking_id}</span>
                </div>
                <PriceDisplay
                    amount={price_summary.total_fare}
                    currency={price_summary.currency}
                />
            </div>

            <div className="p-6">
                {booking.status === BookingStatus.StatusProblematic && (
                    <div className="mb-4 text-sm text-orange-800">
                        Бронь передана на ручной разбор — заявка передана в поддержку
                    </div>
                )}
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-2">
                        {uniqueAirlines.map((airlineCode) => (
                            <TransporterIcon
                                key={airlineCode}
                                name={airlines[airlineCode]?.name}
                                icon={airlines[airlineCode] ? airlines[airlineCode].logo : FlightSvg as any}
                            />
                        ))}
                    </div>

                    <div className="text-center">
                        <div className="text-sm text-gray-500 mb-1">
                            {stops === 0 ? 'Прямой рейс' : `${stops} ${morph(stops, ['пересадка', 'пересадок', 'пересадки'])}`}
                        </div>
                        <div className="text-xs text-gray-400">
                            В пути {Math.floor(totalDuration / 60)}ч {totalDuration % 60}м
                        </div>
                    </div>
                </div>

                <div className="flex items-center justify-between mb-6">
                    <div className="text-start">
                        <AirportCode code={firstSegment.dep_airport_code} />
                        <div className="text-2xl font-bold text-gray-900">
                            <FlightTimeInfo
                                datetime={firstSegment.dep_date_time}
                            />
                        </div>
                    </div>

                    <div className="flex-1 mx-4">
                        <FlightSegmentBar segments={segments} />
                    </div>

                    <div className="text-end">
                        <AirportCode code={lastSegment.arr_airport_code} />
                        <div className="text-2xl font-bold text-gray-900">
                            <FlightTimeInfo
                                datetime={lastSegment.arr_date_time}
                            />
                        </div>
                    </div>
                </div>

                <div className="border-t border-gray-100 pt-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Пассажиры</h4>
                    <div className="space-y-1">
                        {travellers.map((traveller) => (
                            <TravellerInfo key={traveller.id} traveller={traveller} />
                        ))}
                    </div>
                </div>
            </div>
            {showAcknowledge
                ? (
                    <div className="flex items-end justify-between gap-2 p-4 border-t border-gray-100 bg-gray-50">
                        <button onClick={() => openAcknowledgeModal(booking)} className="bg-yellow-500 cursor-pointer text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                            Подтвердить изменение цены
                        </button>
                        {cancelControl}
                    </div>
                )
                : (
                    <div className="flex items-end justify-between gap-2 p-4 border-t border-gray-100 bg-gray-50">
                        {showTicket &&
                            <button onClick={() => openTicketsModal(booking)} className="bg-color-primary cursor-pointer text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                                Билеты
                            </button>
                        }
                        {showPayment &&
                            <button onClick={() => openPaymentModal(booking)} className="bg-color-primary cursor-pointer text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                                Оплатить
                            </button>
                        }
                        {cancelControl}
                    </div>
                )
            }
        </div>
    )
}